import { Button, ButtonTheme } from 'components/Button';
import { ICONS } from 'components/Icon';
import { ModalScreens } from 'components/Navigation/types';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useAlert } from 'hooks/useAlert';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL, OPEN_BARCODE_SCANNER } from 'store/actions';
import {
  doAseFieldInstanceSearchBarcodeForGroup,
  doAseFieldInstanceSearchPin,
  doAseLoadPlans,
} from 'store/assessments/actions';
import {
  selectAseTplFieldGroup,
  selectAseFieldInstancePinsForGroup,
  selectAseFieldInstanceQrsForGroups,
  selectAseFieldGroupStats,
  selectAseIsEditable,
  selectAseFieldInstancesCountForGroup,
  selectAseTplFieldToGroup,
  selectAseIsOnline,
} from 'store/assessments/selectors';
import { InstanceParentParams } from 'store/assessments/types';

type Props = { groupId: string; parent: InstanceParentParams };

export const RepeatableListIntro: React.FC<Props> = ({ groupId, parent }) => {
  const dispatch = useDispatch();
  const alert = useAlert();

  const isEditable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);
  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));
  const parentGroup = useSelector((state) =>
    selectAseTplFieldToGroup(state, parent?.fieldId || '-1'),
  );

  // Get total number of instances in asset and in assessment
  const totalInstanceCount = useSelector((state) =>
    selectAseFieldInstancesCountForGroup(state, {
      groupId,
      parent: null,
      inAssessmentOnly: false,
    }),
  );

  const groupStats = useSelector((state) =>
    selectAseFieldGroupStats(state, {
      groupId,
      parent,
      inAssessmentOnly: true,
    }),
  );

  const groupPins = useSelector((state) =>
    selectAseFieldInstancePinsForGroup(state, {
      groupId,
      parent: !isEditable || isOnline ? parent : null,
      inAssessmentOnly: !isEditable || isOnline,
    }),
  ).map((pin) => {
    if (parentGroup && groupStats.assessmentInstanceIds.includes(pin.id)) {
      return {
        ...pin,
        group: `In ${parentGroup.calculatedInfo.singularName}`,
        color: theme.colors.purple,
      };
    }
    return pin;
  });

  const groupQRs = useSelector((state) =>
    selectAseFieldInstanceQrsForGroups(state, {
      groupId,
      parent: !isEditable || isOnline ? parent : null,
      inAssessmentOnly: !isEditable || isOnline,
    }),
  );

  if (!group) {
    return null;
  }

  const handleFindByQrPress = () =>
    dispatch(
      OPEN_BARCODE_SCANNER({
        text:
          isEditable && !isOnline
            ? `scan to find an existing ${group.calculatedInfo.singularName.toLowerCase()} or to create a new one`
            : `scan to find an existing ${group.calculatedInfo.singularName.toLowerCase()}`,
        actionAfter: doAseFieldInstanceSearchBarcodeForGroup({
          groupId,
          data: '',
          parent,
        }),
      }),
    );

  const handleFindByPlanPress = () =>
    !groupPins.length
      ? alert({
          text: `No ${group.name.toLowerCase()} have pins yet. You'll be able to find them by their pins once there are`,
        })
      : dispatch(
          doAseLoadPlans({
            findNoun: group.calculatedInfo.singularName,
            pins: groupPins,
            actionAfterFind: doAseFieldInstanceSearchPin({
              groupId,
              id: '',
              parent,
            }),
          }),
        );

  const { qrFieldId, pinFieldId } = group.calculatedInfo;

  const showQRButton =
    !!qrFieldId && ((isEditable && !isOnline) || !!groupQRs.length);
  const showPinButton =
    !!pinFieldId && ((isEditable && !isOnline) || !!groupPins.length);

  const unaddedOtherCount =
    isEditable && !isOnline
      ? parent
        ? totalInstanceCount - groupStats.childInstancesCount
        : groupStats.allInstancesCount - groupStats.assessmentInstancesCount
      : 0;

  return (
    <ViewWithSeparators
      style={{ alignItems: 'center' }}
      separator={
        <SeparatorHorizontal
          style={{
            width: 50,
            marginVertical: theme.paddingVertical / 2,
          }}
        />
      }
    >
      {isEditable && !isOnline && (
        <Button
          text="Add New"
          icon={ICONS.PlusLight}
          compact
          buttonTheme={ButtonTheme.themeless}
          onPress={() =>
            dispatch(
              NAVIGATE_MODAL({
                screen: ModalScreens.AseFieldInstance,
                params: { groupId, instanceId: undefined, parent },
              }),
            )
          }
        />
      )}
      {!!unaddedOtherCount && (
        <Button
          text="Add Existing"
          icon={ICONS.PlusLight}
          compact
          buttonTheme={ButtonTheme.themeless}
          onPress={() =>
            dispatch(
              NAVIGATE_MODAL({
                screen: ModalScreens.ASEREPEATABLEINSTANCENEWFROMLIST,
                params: { groupId, parent },
              }),
            )
          }
        />
      )}
      {showQRButton && (
        <Button
          text="Find by QR"
          icon={ICONS.QRCodeLight}
          compact
          buttonTheme={ButtonTheme.themeless}
          onPress={handleFindByQrPress}
        />
      )}
      {showPinButton && (
        <Button
          text="Find on Plan"
          icon={ICONS.LocationPinLight}
          compact
          buttonTheme={ButtonTheme.themeless}
          onPress={handleFindByPlanPress}
        />
      )}
    </ViewWithSeparators>
  );
};
